import {
  calculateIncome,
  cellKey,
  countControlledCapturePoints,
  FACTIONS,
  type GameState,
  getUnitsAt,
  resolveUnitStats,
  TECH_CARD_DEFINITIONS,
  UNIT_DEFINITIONS,
} from "@cloudnest/tri-anger-engine";
import { type CSSProperties, useEffect, useMemo, useState } from "react";
import { navigateTo } from "../../router";
import { TRI_ANGER_REPLAY_FIXTURES } from "./replay-fixtures.generated";
import {
  buildTriAngerReplaySnapshots,
  type TriAngerReplaySnapshot,
} from "./replay-runner";

interface ReplayScreenProps {
  readonly scenario: string;
}

function clampStepIndex(
  index: number,
  snapshots: readonly TriAngerReplaySnapshot[],
): number {
  if (snapshots.length === 0) return 0;
  return Math.min(Math.max(0, index), snapshots.length - 1);
}

function parseInteger(value: string | null): number | null {
  if (value === null || value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) ? parsed : null;
}

function resolveStepIndexFromSearch(
  snapshots: readonly TriAngerReplaySnapshot[],
): number {
  if (typeof window === "undefined" || snapshots.length === 0) return 0;

  const params = new URLSearchParams(window.location.search);
  const sequenceNumber = parseInteger(params.get("seq"));
  if (sequenceNumber !== null) {
    const index = snapshots.findIndex(
      (snapshot) => snapshot.event.sequenceNumber === sequenceNumber,
    );
    if (index >= 0) return index;
  }

  const step = parseInteger(params.get("step"));
  if (step !== null) return clampStepIndex(step, snapshots);

  return 0;
}

function replaceStepQuery(
  index: number,
  snapshots: readonly TriAngerReplaySnapshot[],
): void {
  if (typeof window === "undefined" || snapshots.length === 0) return;

  const url = new URL(window.location.href);
  url.searchParams.set("step", String(index));
  url.searchParams.delete("seq");
  window.history.replaceState(null, "", `${url.pathname}${url.search}`);
}

function phaseLabel(phase: GameState["phase"]): string {
  switch (phase) {
    case "MOVE":
      return "Move";
    case "PRODUCE":
      return "Produce";
    case "COMBAT":
      return "Combat";
    case "OCCUPY":
      return "Occupy";
    case "INCOME":
      return "Income";
    case "GAME_OVER":
      return "Game Over";
  }
}

function tileLabel(kind: string): string {
  if (kind === "home") return "H";
  if (kind === "multi") return "M";
  if (kind === "blocked") return "X";
  if (kind === "capture") return "C";
  return "";
}

function actorName(
  snapshot: TriAngerReplaySnapshot,
  namesByPlayerId: Readonly<Record<string, string>>,
): string {
  return namesByPlayerId[snapshot.event.userId ?? ""] ?? "system";
}

function eventSummary(snapshot: TriAngerReplaySnapshot): string {
  return `#${snapshot.event.sequenceNumber} ${snapshot.event.eventType}`;
}

export function ReplayScreen({ scenario }: ReplayScreenProps) {
  const fixture = TRI_ANGER_REPLAY_FIXTURES[scenario] ?? null;
  const snapshots = useMemo(
    () => (fixture ? buildTriAngerReplaySnapshots(fixture.events) : []),
    [fixture],
  );
  const [stepIndex, setStepIndex] = useState(() =>
    resolveStepIndexFromSearch(snapshots),
  );
  const namesByPlayerId = useMemo(
    () =>
      fixture
        ? Object.fromEntries(
            fixture.players.map((player) => [player.id, player.name]),
          )
        : {},
    [fixture],
  );
  const safeStepIndex = clampStepIndex(stepIndex, snapshots);
  const current = snapshots[safeStepIndex] ?? snapshots[0] ?? null;

  useEffect(() => {
    setStepIndex(resolveStepIndexFromSearch(snapshots));
  }, [snapshots]);

  function setReplayStep(index: number): void {
    const nextIndex = clampStepIndex(index, snapshots);
    setStepIndex(nextIndex);
    replaceStepQuery(nextIndex, snapshots);
  }

  if (!fixture || !current) {
    return (
      <div className="ta-client-shell ta-client-center">
        <section className="ta-client-panel">
          <p className="ta-client-kicker">Tri-Anger Replay</p>
          <h1>리플레이를 찾을 수 없습니다</h1>
          <p>{scenario}</p>
          <button
            className="ta-client-primary-button"
            type="button"
            onClick={() => navigateTo("/")}
          >
            닫기
          </button>
        </section>
      </div>
    );
  }

  const state = current.state;
  const currentPlayer = state.players.find(
    (player) => player.id === state.currentPlayerId,
  );
  const maxIndex = snapshots.length - 1;

  return (
    <div className="ta-client-shell" data-testid="tri-anger-replay-screen">
      <header
        className="ta-client-topbar"
        data-testid="tri-anger-replay-toolbar"
      >
        <div className="ta-client-brand">
          <strong>Tri-Anger Replay</strong>
          <span data-testid="tri-anger-replay-event-summary">
            {eventSummary(current)} · {actorName(current, namesByPlayerId)}
          </span>
          <span data-testid="tri-anger-replay-phase">
            {phaseLabel(state.phase)} · Turn {state.turn} ·{" "}
            {currentPlayer
              ? (namesByPlayerId[currentPlayer.id] ?? currentPlayer.name)
              : "-"}
          </span>
        </div>
        <div className="ta-client-action-grid">
          <button
            className="ta-client-ghost-button"
            data-testid="tri-anger-replay-prev"
            disabled={safeStepIndex === 0}
            type="button"
            onClick={() => setReplayStep(safeStepIndex - 1)}
          >
            Prev
          </button>
          <input
            aria-label="Replay step"
            className="ta-client-replay-slider"
            data-testid="tri-anger-replay-step-slider"
            max={maxIndex}
            min={0}
            type="range"
            value={safeStepIndex}
            onChange={(event) => setReplayStep(Number(event.target.value))}
          />
          <button
            className="ta-client-ghost-button"
            data-testid="tri-anger-replay-next"
            disabled={safeStepIndex === maxIndex}
            type="button"
            onClick={() => setReplayStep(safeStepIndex + 1)}
          >
            Next
          </button>
          <span
            className="ta-client-muted"
            data-testid="tri-anger-replay-step-count"
          >
            {safeStepIndex + 1}/{snapshots.length}
          </span>
        </div>
      </header>

      <main className="ta-client-main">
        <section className="ta-client-board-wrap">
          <ReplayBoard state={state} />
        </section>
        <aside className="ta-client-side">
          <ReplayPlayers namesByPlayerId={namesByPlayerId} state={state} />
          <section className="ta-client-panel">
            <h2>Replay</h2>
            <p data-testid="tri-anger-replay-note">{current.note}</p>
          </section>
          <ReplayLog state={state} />
        </aside>
      </main>

      <footer
        className="ta-client-action-dock"
        data-testid="tri-anger-action-dock"
      >
        {state.phase === "GAME_OVER" ? (
          <>
            <strong data-testid="tri-anger-replay-game-over">
              {state.players.find((player) => player.id === state.winnerId)
                ?.name ?? "Winner"}
            </strong>
            <span>{state.victoryCondition}</span>
          </>
        ) : (
          <>
            <strong>{phaseLabel(state.phase)}</strong>
            <span>Read-only replay</span>
          </>
        )}
      </footer>
    </div>
  );
}

function ReplayPlayers({
  namesByPlayerId,
  state,
}: {
  readonly namesByPlayerId: Readonly<Record<string, string>>;
  readonly state: GameState;
}) {
  return (
    <section className="ta-client-panel">
      <h2>Players</h2>
      <div className="ta-client-player-details">
        {state.players.map((player) => {
          const faction = FACTIONS[player.faction];
          return (
            <article
              className="ta-client-player-card"
              key={player.id}
              style={{ "--ta-player-color": faction.color } as CSSProperties}
            >
              <div className="ta-client-player-card-head">
                <strong>{namesByPlayerId[player.id] ?? player.name}</strong>
                <span>{player.faction}</span>
              </div>
              <div className="ta-client-stat-row">
                <span>Gold {player.gold}</span>
                <span>Income {calculateIncome(state, player.id)}</span>
                <span>
                  CP {countControlledCapturePoints(state, player.id)}/8
                </span>
              </div>
              <div className="ta-client-tech-row">
                {player.ownedTech.length === 0 ? (
                  <span className="ta-client-muted">No tech</span>
                ) : (
                  player.ownedTech.map((tech) => (
                    <span data-revealed={tech.revealed} key={tech.id}>
                      {TECH_CARD_DEFINITIONS[tech.id].name}
                    </span>
                  ))
                )}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function ReplayBoard({ state }: { readonly state: GameState }) {
  return (
    <div className="ta-client-board" data-testid="tri-anger-board">
      {state.board.map((tile) => {
        const units = getUnitsAt(state, tile.position);
        return (
          <button
            className="ta-client-cell"
            data-kind={tile.kind}
            data-legal={false}
            data-testid={`tri-anger-cell-${tile.position.x}-${tile.position.y}`}
            disabled={true}
            key={cellKey(tile.position)}
            type="button"
          >
            <span className="ta-client-cell-label">{tileLabel(tile.kind)}</span>
            <span className="ta-client-cell-control">
              {state.controlByCell[cellKey(tile.position)] ? "*" : ""}
            </span>
            <span className="ta-client-unit-stack">
              {units.slice(0, 4).map((unit) => {
                const definition = UNIT_DEFINITIONS[unit.typeId];
                const owner = state.players.find(
                  (player) => player.id === unit.ownerId,
                );
                const faction = owner ? FACTIONS[owner.faction] : undefined;
                const stats = resolveUnitStats(state, unit);
                return (
                  <span
                    className="ta-client-unit-chip"
                    key={unit.id}
                    style={
                      {
                        "--ta-unit-color": faction?.color ?? "#64748b",
                      } as CSSProperties
                    }
                    title={`${definition.name} HP ${unit.hp}/${stats.hp}`}
                  >
                    {definition.name.slice(0, 1)}
                    <small>{unit.hp}</small>
                  </span>
                );
              })}
              {units.length > 4 ? (
                <span className="ta-client-unit-more">+{units.length - 4}</span>
              ) : null}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function ReplayLog({ state }: { readonly state: GameState }) {
  const entries = [...state.log].slice(-8).reverse();
  return (
    <section className="ta-client-panel ta-client-log">
      <h2>Log</h2>
      {entries.map((entry, index) => (
        <p key={`${entry.turn}-${entry.phase}-${index}`}>
          <span>{entry.phase}</span>
          {entry.message}
        </p>
      ))}
    </section>
  );
}
